import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { AnalyticsEventRow, dailyBuckets } from "@/hooks/useAnalyticsConversao";
import { AnalyticsPeriod, periodBucketCount } from "@/utils/analyticsPeriod";

interface Props {
  events: AnalyticsEventRow[];
  period: AnalyticsPeriod;
}

const ChartsBlock: React.FC<Props> = ({ events, period }) => {
  const days = Math.max(periodBucketCount(period), 1);

  const series = useMemo(() => {
    const visits = dailyBuckets(events, (e) => e.event_type === "page_view", days);
    const whats = dailyBuckets(
      events,
      (e) => e.event_type === "whatsapp_click" || e.event_type === "direct_payment_whatsapp_click",
      days
    );
    const nubank = dailyBuckets(events, (e) => e.event_type === "direct_payment_nubank_click", days);
    const stripe = dailyBuckets(
      events,
      (e) => e.event_type === "checkout_click" && e.metadata?.origin === "planos_stripe",
      days
    );

    return visits.map((v, i) => ({
      date: v.date,
      visitas: v.count,
      whatsapp: whats[i].count,
      nubank: nubank[i].count,
      stripe: stripe[i].count,
    }));
  }, [events, days]);

  const deviceData = useMemo(() => {
    const m = events.filter((e) => e.device === "mobile").length;
    const d = events.filter((e) => e.device === "desktop").length;
    return [
      { name: "Mobile", value: m, color: "hsl(var(--primary))" },
      { name: "Desktop", value: d, color: "hsl(var(--muted-foreground))" },
    ];
  }, [events]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Eventos por dia</CardTitle>
        </CardHeader>
        <CardContent className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="visitas" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="whatsapp" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="nubank" stroke="#a855f7" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="stripe" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Mobile vs Desktop</CardTitle>
        </CardHeader>
        <CardContent className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={deviceData} dataKey="value" nameKey="name" outerRadius={90} label>
                {deviceData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChartsBlock;
